/**
 * Entity insights — pure selectors that summarise everything Atlas knows
 * about a single entity in one shot (doc 147 §5.3 world-class redesign).
 *
 * Two-tier API so we don't pay for lint + refactor on every entity sheet
 * open:
 *
 *   const index = createInsightIndex(manifest); // once per manifest
 *   const ins = entityInsights(index, 'Account'); // cheap per-entity lookup
 *
 * Powers three downstream surfaces: the answer tiles in the entity sheet,
 * the entity command palette, and the hover previews. All three consume
 * the same numbers so the user never sees the data disagree with itself.
 */
import type { AtlasManifest, CloudFunction, CloudFunctionKind } from '@atlas-manifest/types';
import { runLint, type LintFinding } from '@atlas-manifest/lint';
import { impactTree, impactedDomains } from '../graph/pathfind.js';
import { buildRefactorProposals, type RefactorProposal } from './refactorProposals.js';

export interface BlastTier {
  depth: number;
  count: number;
  label: string;
  /** CSS colour for the BlastBars primitive. */
  color: string;
}

export interface PatternSummary {
  name: string;
  kind: CloudFunctionKind;
}

export interface EntityInsights {
  entityId: string;
  blast: {
    entityCount: number;
    domainCount: number;
    crossDomainHopCount: number;
    tiers: BlastTier[];
  };
  patterns: {
    readers: PatternSummary[];
    writers: PatternSummary[];
    readerCount: number;
    writerCount: number;
  };
  quality: {
    lintFindings: number;
    refactorProposals: number;
    /** Tags this entity carries beginning with `pii:` (e.g. pii:email). */
    piiTags: string[];
    /** Count of entities transitively reachable from this one through references. */
    piiDownstream: number;
    /** True iff lint clean, no refactor proposals, no PII downstream concerns. */
    clean: boolean;
  };
  /**
   * Sandbox-cluster summary (PR P1). A "simulatable trigger" is any cloud
   * function that writes to this entity — those are the natural starting
   * points for a What-If sandbox or Simulation run. `canSeed` reflects
   * whether the entity is persisted (sandboxing an ephemeral entity has
   * no real-world counterpart, so the Sandbox CTA is gated).
   */
  sandbox: {
    simulatableTriggers: number;
    canSeed: boolean;
  };
  /**
   * History-cluster summary (PR P1). Derived from the entity's `docRef`
   * numeric prefix — the same proxy {@link buildDriftTimeline} uses. A
   * future iteration will wire this to real manifest-diff history.
   */
  history: {
    earliestDoc: number | null;
    latestDoc: number | null;
    /** `latest - earliest` when both known, otherwise 0. */
    docSpan: number;
  };
}

const PATTERN_TOP_K = 3;

function blastLabel(depth: number): string {
  if (depth === 1) return 'direct';
  if (depth === 2) return 'cascade';
  return `depth ${depth}`;
}

function blastTone(depth: number): string {
  if (depth <= 1) return 'var(--danger)';
  if (depth === 2) return 'var(--caution)';
  return 'var(--info)';
}

/** Manifest-level data pre-computed once and reused across entity lookups. */
export interface InsightIndex {
  manifest: AtlasManifest;
  lintByEntity: Map<string, LintFinding[]>;
  refactorByEntity: Map<string, RefactorProposal[]>;
  cloudFnsByReadTarget: Map<string, CloudFunction[]>;
  cloudFnsByWriteTarget: Map<string, CloudFunction[]>;
  /** entityId → reachable downstream entity ids (for PII cone). */
  downstream: Map<string, Set<string>>;
}

export function createInsightIndex(manifest: AtlasManifest): InsightIndex {
  const lint = runLint(manifest);
  const lintByEntity = new Map<string, LintFinding[]>();
  for (const f of lint.findings) {
    const arr = lintByEntity.get(f.ref) ?? [];
    arr.push(f);
    lintByEntity.set(f.ref, arr);
  }
  const refactor = buildRefactorProposals(manifest);
  const refactorByEntity = new Map<string, RefactorProposal[]>();
  for (const r of refactor) {
    const arr = refactorByEntity.get(r.entityId) ?? [];
    arr.push(r);
    refactorByEntity.set(r.entityId, arr);
  }

  const cloudFnsByReadTarget = new Map<string, CloudFunction[]>();
  const cloudFnsByWriteTarget = new Map<string, CloudFunction[]>();
  for (const cf of manifest.cloudFunctions ?? []) {
    for (const r of cf.reads) {
      const arr = cloudFnsByReadTarget.get(r) ?? [];
      arr.push(cf);
      cloudFnsByReadTarget.set(r, arr);
    }
    for (const w of cf.writes) {
      const arr = cloudFnsByWriteTarget.get(w) ?? [];
      arr.push(cf);
      cloudFnsByWriteTarget.set(w, arr);
    }
  }

  // PII cone semantics: starting from an entity that carries PII, find
  // everyone who *reads* it — i.e. transitively reaches it via FKs. That's
  // the same reverse-adjacency walk as `impactTree`, so we precompute it
  // here as a set per entity (cheap; we use this for both PII cone count
  // and any future "where does this data flow?" answers).
  const reverseAdj = new Map<string, string[]>();
  for (const ref of manifest.references) {
    const arr = reverseAdj.get(ref.to) ?? [];
    arr.push(ref.from);
    reverseAdj.set(ref.to, arr);
  }
  const downstream = new Map<string, Set<string>>();
  for (const e of manifest.entities) {
    const seen = new Set<string>();
    const stack = [...(reverseAdj.get(e.id) ?? [])];
    while (stack.length > 0) {
      const next = stack.pop()!;
      if (seen.has(next)) continue;
      seen.add(next);
      for (const nb of reverseAdj.get(next) ?? []) stack.push(nb);
    }
    downstream.set(e.id, seen);
  }

  return {
    manifest,
    lintByEntity,
    refactorByEntity,
    cloudFnsByReadTarget,
    cloudFnsByWriteTarget,
    downstream,
  };
}

export function entityInsights(index: InsightIndex, entityId: string): EntityInsights {
  const { manifest } = index;
  const entity = manifest.entities.find((e) => e.id === entityId);

  // Blast
  const impact = entity ? impactTree(manifest, entityId) : [];
  const maxDepth = impact.reduce((m, n) => Math.max(m, n.depth), 0);
  const tiers: BlastTier[] = [];
  for (let depth = 1; depth <= maxDepth; depth++) {
    const count = impact.filter((n) => n.depth === depth).length;
    if (count > 0) tiers.push({ depth, count, label: blastLabel(depth), color: blastTone(depth) });
  }
  const blast = {
    entityCount: impact.length,
    domainCount: impactedDomains(impact).length,
    crossDomainHopCount: impact.filter((n) => n.crossDomain).length,
    tiers,
  };

  // Patterns
  const readers = (index.cloudFnsByReadTarget.get(entityId) ?? []).map((cf) => ({
    name: cf.name,
    kind: cf.kind,
  }));
  const writers = (index.cloudFnsByWriteTarget.get(entityId) ?? []).map((cf) => ({
    name: cf.name,
    kind: cf.kind,
  }));
  const patterns = {
    readers: readers.slice(0, PATTERN_TOP_K),
    writers: writers.slice(0, PATTERN_TOP_K),
    readerCount: readers.length,
    writerCount: writers.length,
  };

  // Quality
  const piiTags = entity ? entity.tags.filter((t) => t.startsWith('pii:')) : [];
  const piiDownstream = piiTags.length > 0 ? (index.downstream.get(entityId)?.size ?? 0) : 0;
  const lintFindings = index.lintByEntity.get(entityId)?.length ?? 0;
  const refactorProposals = index.refactorByEntity.get(entityId)?.length ?? 0;
  const quality = {
    lintFindings,
    refactorProposals,
    piiTags,
    piiDownstream,
    clean: lintFindings === 0 && refactorProposals === 0 && piiTags.length === 0,
  };

  // Sandbox (PR P1) — writers double as simulation entry points. `canSeed`
  // reflects whether sandboxing makes sense for this entity.
  const sandbox = {
    simulatableTriggers: patterns.writerCount,
    canSeed: Boolean(entity?.isPersisted),
  };

  // History (PR P1) — derive a doc-number range from the entity's docRef
  // prefix. v2 will wire real manifest-diff history.
  const docNum = entity ? parseDocNumber(entity.docRef) : null;
  const history = {
    earliestDoc: docNum,
    latestDoc: docNum,
    docSpan: 0,
  };

  return { entityId, blast, patterns, quality, sandbox, history };
}

/** Parse the numeric prefix of a docRef, e.g. `14-account-model` → 14. */
function parseDocNumber(docRef: string): number | null {
  const m = docRef.match(/^(\d+)/);
  return m ? Number(m[1]) : null;
}
